import { AppData, DocumentServicesObject } from '@wix/document-services-types';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import { SelectedComponentData } from '../utils/handlerUtils';

export interface EditorPublicDataHandlers {
  setValue: TPAHandler;
  getValue: TPAHandler;
  removeValue: TPAHandler;
}

export type PublicDataHandlerType = keyof EditorPublicDataHandlers;

export const createEditorPublicDataHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
): EditorPublicDataHandlers => {
  const setValue = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message for setValue handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error('Component pointer not found for setValue');
    }

    if (platformApiMethods.document.tpa.data) {
      const updatedStyles = await platformApiMethods.document.tpa.data.set(
        {} as AppData,
        '',
        {
          compRef: compPointer,
          key: msg.data?.key,
          value: msg.data?.value,
          scope: msg.data?.scope,
        },
      );

      callback(updatedStyles);
    } else {
      // fallback to documentServicesAPI because there's no tpa.data.set in platformApiMethods in Santa editor
      documentServicesAPI.tpa.data.set(
        compPointer,
        msg.data?.key,
        msg.data?.value,
        msg.data?.scope,
        callback,
      );
    }
  };

  const getValue = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (msg.data?.scope === documentServicesAPI.tpa.data.SCOPE.APP) {
      const [selectedComp] =
        await platformApiMethods.editor.selection.getSelectedComponents();

      if (!selectedComp) {
        throw new Error('Component should be selected for getValue handler');
      }

      const origCompData =
        documentServicesAPI.components.data.get(selectedComp);
      documentServicesAPI.tpaV2.data.app.get(
        origCompData.appDefId,
        msg.data?.key,
        callback,
      );
    } else {
      const compPointer = documentServicesAPI.components.get.byId(msg.compId);

      if (!compPointer) {
        throw new Error('Component pointer not found for getValue');
      }

      documentServicesAPI.tpa.data.comp.get(
        compPointer,
        msg.data?.key,
        callback,
      );
    }
  };

  const removeValue = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message for removeValue handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error('Component pointer not found for removeValue');
    }

    if (platformApiMethods.document.tpa.data) {
      const updatedStyles = await platformApiMethods.document.tpa.data.remove(
        {} as AppData,
        '',
        {
          compRef: compPointer,
          key: msg.data?.key,
          scope: msg.data?.scope,
        },
      );

      callback(updatedStyles);
    } else {
      // fallback to documentServicesAPI because there's no tpa.data.remove in platformApiMethods in Santa editor
      documentServicesAPI.tpa.data.remove(
        compPointer,
        msg.data?.key,
        msg.data?.scope,
        callback,
      );
    }
  };

  return {
    setValue,
    getValue,
    removeValue,
  };
};
